import { createIndex } from "../omnia"
import { selectAnalysis } from "./analysis/generic"
import { getFieldSoilAssessments } from "./analysis/sa"
import { getFieldAnalysisGroups } from "./analysis/std"
import { AnalysisType } from "./analysis/type"
import { addNewAction, updateAction } from "./observations/actions"
import { saveUserObservation } from "./observations/manager"
import { addNewObservation, deleteObservation, hasObservationChanged, setupFieldObservations, updateObservation } from "./observations/observations"
import { SelectedFieldObservation } from "./observations/types"
import { loadApiData } from "./state"
import { PlanApiData, PlanField, PlanState } from "./types"

export type PlanActionDispatch = React.Dispatch<PlanAction>

export type PlanAction =
    {
        type: "selectField"
        selectedFieldId: number
    }
    | {
        type: "selectObservation"
        observation: number
        action?: number
    }
    | {
        type: "load",
        data: PlanApiData
    }
    | {
        type: "refreshField"
        field: PlanField
    }
    | {
        type: "updateObservation"
        key: number
        text: string
    }
    | {
        type: "updateAction"
        obs: SelectedFieldObservation,
        key: number
        text: string
    }
    | {
        type: "deleteObservation"
        key: number
        delete: boolean
    }
    | {
        type: "addNewObservation"
    }
    | {
        type: "addNewAction"
        obs: SelectedFieldObservation
    }
    | {
        type: "saveUserObservation",
        observation: string,
        action?: string,
        save: boolean
    }
    | {
        type: "selectAnalysis",
        target: AnalysisType,
        selected: boolean
    }
    | {
        type: "cancelChanges"
    }
    | {
        type: "setPredefsFilter",
        filter: string
    }
    | {
        type: "updateAnalysisSettings",
        update: Partial<PlanState["settings"]["analysis"]>
    }


export function updateState(state: PlanState, action: PlanAction): PlanState {

    switch (action.type) {

        case "load":
            return loadApiData(state, action.data)

        case "selectField":
            return selectField(state, action.selectedFieldId)

        case "updateObservation":
            return reviewUpdatedState(updateObservation(state, action))

        case "selectObservation":
            return selectObservation(state, action)

        case "updateAction":
            return reviewUpdatedState(updateAction(state, action))

        case "deleteObservation":
            return reviewUpdatedState(deleteObservation(state, action))

        case "addNewObservation":
            return reviewUpdatedState(addNewObservation(state))

        case "addNewAction":
            return reviewUpdatedState(addNewAction(state, action.obs))

        case "saveUserObservation":
            {
                const userObservations = saveUserObservation(state.userObservations, action)
                return { ...state, userObservations }
            }

        case "selectAnalysis":
            return reviewUpdatedState(selectAnalysis(state, action.target, action.selected))

        case "refreshField":
            return refreshField(state, action.field)

        case "cancelChanges":
            return selectField(state, state?.selectedField?.fieldId)

        case "setPredefsFilter":
            return { ...state, settings: { ...state.settings, predefsFilter: action.filter } }

        case "updateAnalysisSettings":
            return {
                ...state,
                settings: {
                    ...state.settings,
                    analysis: {
                        ...state.settings.analysis,
                        ...action.update
                    }
                }
            }

        default:
            return state

    }


}

function reviewUpdatedState(state: PlanState): PlanState {

    const { selectedField: field, fields } = state

    if (!field) {
        return state
    }

    const initField = fields[field.fieldId]

    let { observations } = field
    const { analysis } = field

    observations = observations.map(o => {

        const updated = (!o.id)
            ? false
            : hasObservationChanged(o, initField?.observations?.find(io => io.id === o.id))

        return (updated === o.updated)
            ? o
            : { ...o, updated }

    })

    const updated =
        observations.some(o => (o.new || o.updated || o.deleted) && !(o.new && o.deleted))
        || analysis.std.some(s => s.selected.value !== s.selected.initial)
        || analysis.sa.some(s => s.selected.value !== s.selected.initial)
    // 220608 - Not used anymore, healthy soil assessments are not included in soil mgmt plans
    // || analysis.hsa.some(s => s.selected.value !== s.selected.initial)

    const valid = observations.every(o => o.valid || o.deleted)

    state = {
        ...state,
        selectedField: {
            ...field,
            observations,
            updated,
            valid
        }
    }

    return state

}

function refreshField(state: PlanState, field: PlanField): PlanState {

    const { fieldId } = field
    const { fields, selectedField, year, plans } = state

    const updatedFields = Object.values(fields)
        .filter(f => f.fieldId !== fieldId)
        .concat(field)

    const yearEmpty = updatedFields.every(f => !(f.observations?.length || f.analyses?.some(a => !a.report)))

    const updatedPlans = yearEmpty
        ? plans.filter(p => p !== year)
        : plans.concat([year]).unique(a => a)

    state = {
        ...state,
        plans: updatedPlans,
        fields: createIndex(updatedFields, f => f.fieldId)
    }

    return (selectedField?.fieldId === fieldId)
        ? selectField(state, fieldId)
        : state

}

function selectObservation(state: PlanState, { observation, action }: { observation: number, action?: number }): PlanState {

    return {
        ...state,
        selectedField: {
            ...state.selectedField,
            activeObservation: { observation, action },
        }
    }

}

export function selectField(state: PlanState, fieldId: number): PlanState {

    const { fields, year } = state

    const field: PlanField = fieldId
        ? (
            fields[fieldId]
            ?? {
                fieldId,
                year,
                observations: [],
                analyses: []
            }
        )
        : undefined

    const keyAllocator = getKeyAllocator()

    const observations = field ? setupFieldObservations(field?.observations, keyAllocator, state.observations) : []

    const selectedField: PlanState["selectedField"] = fieldId
        ? {
            fieldId,
            year,
            keyAllocator,
            activeObservation: undefined,
            observations,
            analysis: {
                std: getFieldAnalysisGroups(fieldId, state.analysis.std, field),
                sa: getFieldSoilAssessments(fieldId, state.analysis.sa, field),
                // 220609 - Not used anymore, healthy soil assessments are not included in soil mgmt plans
                // hsa: getFieldHsa(fieldId, state.analysis.hsa, field),
                hsa: []
            },
            updated: false,
            valid: true
        }
        : undefined

    return {
        ...state,
        selectedField,
    }

}

function getKeyAllocator() {
    let key = 0
    return () => ++key
}
